import { useState, useEffect, useRef } from 'react';
import { MapPin, Clock, Loader2 } from 'lucide-react';
import { usePlaceAutocomplete } from '../../hooks/useClaims';

interface LocationAutocompleteProps {
  id: string;
  placeholder?: string;
  value: string;
  onChange: (val: string) => void;
  className?: string;
}

const RECENTS_KEY = 'rhpc:recent-destinations';

export function getRecentDestinations(): string[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

export function saveRecentDestination(address: string) {
  if (!address || !address.trim()) return;
  const trimmed = address.trim();
  try {
    let recents = getRecentDestinations();
    recents = recents.filter((r) => r.toLowerCase() !== trimmed.toLowerCase());
    recents.unshift(trimmed);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(recents.slice(0, 5))); // Keep last 5
  } catch (err) {
    console.error('Failed to save recent destination:', err);
  }
}

export function LocationAutocomplete({
  id,
  placeholder,
  value,
  onChange,
  className = '',
}: LocationAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [query, setQuery] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);
  const autocomplete = usePlaceAutocomplete();

  // Sync internal query state with parent value
  useEffect(() => {
    setQuery(value);
  }, [value]);

  // Debounce API calls
  useEffect(() => {
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const res = await autocomplete.mutateAsync(query);
        setSuggestions(res.predictions || []);
      } catch {
        setSuggestions([]);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [query]);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const recents = getRecentDestinations();
  const showRecents = !query.trim() && recents.length > 0;
  const items = showRecents ? recents : suggestions;

  const handleSelect = (val: string) => {
    onChange(val);
    setQuery(val);
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown') {
        setIsOpen(true);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < items.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < items.length) {
        handleSelect(items[activeIndex]!);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div ref={containerRef} className="relative flex-1">
      <input
        id={id}
        type="text"
        placeholder={placeholder}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          onChange(e.target.value);
          setIsOpen(true);
          setActiveIndex(-1);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        className={
          className ||
          'h-10 w-full rounded-md border border-input bg-surface px-3 py-2 text-sm placeholder:text-text-2 focus:outline-2 focus:outline-primary focus:outline-offset-1 disabled:cursor-not-allowed disabled:opacity-50'
        }
      />

      {isOpen && (suggestions.length > 0 || showRecents || autocomplete.isPending) && (
        <div className="absolute left-0 right-0 z-50 mt-1 max-h-60 overflow-y-auto rounded-lg border border-border bg-surface text-popover-foreground shadow-lg">
          {autocomplete.isPending && (
            <div className="flex items-center justify-center p-3 text-xs text-text-2 gap-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              Searching locations...
            </div>
          )}

          {!autocomplete.isPending && showRecents && (
            <div className="px-3 pt-2 pb-1 text-2xs font-semibold uppercase tracking-wider text-text-2">
              Recent Destinations
            </div>
          )}

          {!autocomplete.isPending && items.map((item, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => handleSelect(item)}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors outline-none ${
                idx === activeIndex
                  ? 'bg-primary-soft text-primary-text'
                  : 'hover:bg-surface-2 text-text'
              }`}
            >
              {showRecents ? (
                <Clock className="h-3.5 w-3.5 text-text-2 shrink-0" />
              ) : (
                <MapPin className="h-3.5 w-3.5 text-primary shrink-0" />
              )}
              <span className="truncate">{item}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
